import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './TicTacToe.css';

const TicTacToe = () => {
    const [board, setBoard] = useState(Array(9).fill(null));
    const [isPlayerTurn, setIsPlayerTurn] = useState(true);
    const [winner, setWinner] = useState(null); // 'X', 'O', 'Draw', or null
    const [winningLine, setWinningLine] = useState([]);

    // Sound effects (optional, can be added later)
    // const playSound = (type) => { ... }

    const checkWinner = (squares) => {
        const lines = [
            [0, 1, 2],
            [3, 4, 5],
            [6, 7, 8],
            [0, 3, 6],
            [1, 4, 7],
            [2, 5, 8],
            [0, 4, 8],
            [2, 4, 6],
        ];

        for (let i = 0; i < lines.length; i++) {
            const [a, b, c] = lines[i];
            if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
                return { winner: squares[a], line: lines[i] };
            }
        }
        return null;
    };

    const handleClick = (index) => {
        if (board[index] || winner || !isPlayerTurn) return;

        const newBoard = [...board];
        newBoard[index] = 'X';
        setBoard(newBoard);
        setIsPlayerTurn(false);

        const result = checkWinner(newBoard);
        if (result) {
            setWinner(result.winner);
            setWinningLine(result.line);
        } else if (!newBoard.includes(null)) {
            setWinner('Draw');
        }
    };

    // Simple AI (Opponent)
    useEffect(() => {
        if (!isPlayerTurn && !winner) {
            const timer = setTimeout(() => {
                const emptyIndices = board.map((val, idx) => val === null ? idx : null).filter(val => val !== null);

                if (emptyIndices.length > 0) {
                    // 1. Try to win
                    // 2. Block player
                    // 3. Pick random (making it beatable)

                    let move = null;

                    // Simple "Random" AI to ensure it's beatable but not totally braindead
                    // To make it easy: 50% chance to play optimally (block/win), 50% random
                    const makeMistake = Math.random() > 0.7;

                    if (!makeMistake) {
                        // Try to find a winning move for O
                        for (let idx of emptyIndices) {
                            const testBoard = [...board];
                            testBoard[idx] = 'O';
                            if (checkWinner(testBoard)?.winner === 'O') {
                                move = idx;
                                break;
                            }
                        }

                        // Use a blocking move if no win found
                        if (move === null) {
                            for (let idx of emptyIndices) {
                                const testBoard = [...board];
                                testBoard[idx] = 'X';
                                if (checkWinner(testBoard)?.winner === 'X') {
                                    move = idx;
                                    break;
                                }
                            }
                        }
                    }

                    if (move === null) {
                        const randomIndex = Math.floor(Math.random() * emptyIndices.length);
                        move = emptyIndices[randomIndex];
                    }

                    const newBoard = [...board];
                    newBoard[move] = 'O';
                    setBoard(newBoard);

                    const result = checkWinner(newBoard);
                    if (result) {
                        setWinner(result.winner);
                        setWinningLine(result.line);
                    } else if (!newBoard.includes(null)) {
                        setWinner('Draw');
                    }
                    setIsPlayerTurn(true);
                }
            }, 600); // Artificial delay for "thinking"
            return () => clearTimeout(timer);
        }
    }, [isPlayerTurn, winner, board]);

    const resetGame = React.useCallback(() => {
        setBoard(Array(9).fill(null));
        setWinner(null);
        setWinningLine([]);
        setIsPlayerTurn(true);
    }, []);

    // Placeholder S3 URL - User needs to update this
    const RESUME_URL = "https://resume-aniket.s3.eu-north-1.amazonaws.com/Aniket_Shetty_Resume_%2B353892016049.pdf";

    // Auto-restart on loss (AI wins) or Draw
    useEffect(() => {
        if (winner === 'O' || winner === 'Draw') {
            const timer = setTimeout(() => {
                resetGame();
            }, 1500); // 1.5s delay to read the message
            return () => clearTimeout(timer);
        }
    }, [winner, resetGame]);

    return (
        <div className="tic-tac-toe-container" id="game">
            <h2 className="game-title">
                BEAT ME TO UNLOCK A PRIZE
            </h2>

            <div className="game-board">
                {board.map((cell, index) => (
                    <motion.div
                        key={index}
                        className={`cell ${cell} ${winningLine.includes(index) ? 'winner-cell' : ''}`}
                        onClick={() => handleClick(index)}
                        whileHover={{ scale: !cell && !winner ? 1.05 : 1, backgroundColor: !cell && !winner ? "rgba(255,255,255,0.05)" : undefined }}
                        whileTap={{ scale: 0.95 }}
                    >
                        {cell === 'X' && (
                            <motion.span
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                            >
                                ✕
                            </motion.span>
                        )}
                        {cell === 'O' && (
                            <motion.span
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                            >
                                ◯
                            </motion.span>
                        )}
                    </motion.div>
                ))}

            </div>

            <div className="game-status">
                {!winner && (
                    <p>{isPlayerTurn ? "YOUR TURN (X)" : "ANALYZING..."}</p>
                )}
                {winner && (
                    <AnimatePresence mode='wait'>
                        <motion.div
                            className="result-overlay"
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                        >
                            {winner === 'X' ? (
                                <div className="win-message">
                                    <h3>Hurray! You have won!</h3>

                                    <div className="win-actions">
                                        <motion.a
                                            href={RESUME_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="download-btn"
                                            whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(255,255,255,0.5)" }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            View your prize!
                                        </motion.a>

                                        <button onClick={resetGame} className="retry-btn-small">
                                            Play Again
                                        </button>
                                    </div>
                                </div>
                            ) : winner === 'O' ? (
                                <div className="lose-message">
                                    <h3>You lost! Restarting...</h3>
                                </div>
                            ) : (
                                <div className="draw-message">
                                    <h3>It's a draw! Restarting...</h3>
                                </div>
                            )}
                        </motion.div>
                    </AnimatePresence>
                )}
            </div>
        </div>
    );
};

export default TicTacToe;
